import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Button, ConfigProvider, Flex, Typography } from 'antd';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import {
  OnboardingBillingStep,
  OnboardingBusinessTypeStep,
  OnboardingCompleteStep,
  OnboardingMcpStep,
  OnboardingProfileStep,
  OnboardingSurveyStep,
  OnboardingWorkspaceStep,
} from '../../../modules/onboarding/components';
import {
  ONBOARDING_STEP_FORM_ID,
  nextOnboardingStep,
  onboardingStepIndex,
  onboardingSteps,
  previousOnboardingStep,
} from '../../../modules/onboarding/constants';
import { useOnboardingContext, useOnboardingStepSubmit } from '../../../modules/onboarding/hooks';
import { storage } from '../../../ui/utils/local-storage';

import type { GetAccountResponseDto } from '../../../api';
import type { OnboardingStepKey } from '../../../modules/onboarding/constants';
import type { OnboardingStepContentProps } from '../../../modules/onboarding/hooks';

const OnboardingStepRoute = () => {
  const { step } = Route.useParams();
  const stepKey = step as OnboardingStepKey;
  const navigate = useNavigate();
  const { organizationId, membership, onboarding } = useOnboardingContext();
  const submit = useOnboardingStepSubmit(organizationId ?? '');
  const [sampleDataRequested, setSampleDataRequested] = useState(
    onboarding?.sampleDataRequested ?? false,
  );

  if (step === 'complete') {
    return <OnboardingCompleteStep sampleDataRequested={sampleDataRequested} submit={submit} />;
  }

  const currentStep = onboardingSteps.find((candidate) => candidate.key === stepKey)!;
  const currentIndex = onboardingStepIndex(stepKey);
  const nextStep = nextOnboardingStep(stepKey);
  const hasActiveSubscription = Boolean(membership?.organization.hasActiveSubscription);
  const showSkip =
    currentStep.skip &&
    !(stepKey === 'workspace' && membership?.connections?.length) &&
    !(stepKey === 'subscription' && hasActiveSubscription);
  const showSubmit = !(stepKey === 'subscription' && !hasActiveSubscription);
  const contentProps: OnboardingStepContentProps = { submit, nextStep };

  const handleSkip = () => submit.submitStep({}, nextStep);

  return (
    <ConfigProvider
      theme={{
        components: {
          Input: {
            colorBgContainer: 'var(--ant-color-fill-tertiary)',
            colorBorder: 'var(--ant-color-fill-tertiary)',
            hoverBg: 'var(--ant-color-fill-tertiary)',
            hoverBorderColor: 'var(--ant-color-fill-tertiary)',
            activeBg: 'var(--ant-color-fill-tertiary)',
            activeShadow: '0 0 0 3px var(--ant-color-fill-secondary)',
            errorActiveShadow: '0 0 0 3px var(--ant-color-error-bg)',
            colorTextPlaceholder: 'var(--ant-color-text-tertiary)',
          },
          Select: {
            colorErrorBg: 'var(--ant-color-fill-tertiary)',
            colorErrorBgHover: 'var(--ant-color-fill-tertiary)',
            colorTextPlaceholder: 'var(--ant-color-text-tertiary)',
          },
          Form: { fontSize: 12 },
        },
      }}
    >
      <Flex
        vertical
        gap={34}
        style={{
          width: (stepKey === 'subscription' && 480) || (stepKey === 'mcp' && 520) || 400,
          maxWidth: '100%',
        }}
      >
        <Flex justify="center" gap={6}>
          {onboardingSteps.map((candidate, index) => (
            <span
              key={candidate.key}
              style={{
                width: 28,
                height: 4,
                borderRadius: 2,
                background:
                  index <= currentIndex
                    ? 'var(--ant-color-text)'
                    : 'var(--ant-color-fill-secondary)',
              }}
            />
          ))}
        </Flex>
        <Flex vertical gap={16}>
          <Flex vertical align="center">
            <Typography.Title
              level={3}
              style={{ textAlign: 'center', fontSize: 24, textWrap: 'balance' }}
            >
              {currentStep.title}
            </Typography.Title>
            <Typography.Paragraph
              type="secondary"
              style={{ textAlign: 'center', fontSize: 14, marginBottom: 0, textWrap: 'balance' }}
            >
              {currentStep.subtitle}
            </Typography.Paragraph>
          </Flex>
          {currentStep.surveyFields ? (
            <OnboardingSurveyStep
              key={stepKey}
              {...contentProps}
              fields={currentStep.surveyFields}
            />
          ) : stepKey === 'profile' ? (
            <OnboardingProfileStep {...contentProps} />
          ) : stepKey === 'business-type' ? (
            <OnboardingBusinessTypeStep {...contentProps} />
          ) : stepKey === 'subscription' ? (
            <OnboardingBillingStep {...contentProps} />
          ) : stepKey === 'mcp' ? (
            <OnboardingMcpStep {...contentProps} />
          ) : (
            <OnboardingWorkspaceStep
              {...contentProps}
              sampleDataRequested={sampleDataRequested}
              setSampleDataRequested={setSampleDataRequested}
            />
          )}
          <Flex align="center" justify="space-between">
            {currentStep.back ? (
              <Button
                variant="text"
                color="default"
                icon={<ArrowLeft size="1em" />}
                style={{ fontSize: 14 }}
                onClick={() =>
                  navigate({
                    to: '/onboarding/$step',
                    params: { step: previousOnboardingStep(stepKey) },
                  })
                }
              >
                Back
              </Button>
            ) : (
              <span />
            )}
            <Flex gap={8}>
              {showSkip ? (
                <Button
                  variant="filled"
                  color="default"
                  data-testid="onboarding-skip"
                  onClick={handleSkip}
                  loading={submit.isPending}
                  style={{ fontSize: 14 }}
                >
                  Skip for now
                </Button>
              ) : null}
              {showSubmit ? (
                <Button
                  type="primary"
                  htmlType="submit"
                  data-testid="onboarding-continue"
                  form={ONBOARDING_STEP_FORM_ID}
                  icon={<ArrowRight size="1em" />}
                  iconPlacement="end"
                  loading={submit.isPending}
                  style={{ fontSize: 14 }}
                >
                  {currentStep.submitLabel}
                </Button>
              ) : null}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </ConfigProvider>
  );
};

export const Route = createFileRoute('/_authenticated/onboarding/$step')({
  validateSearch: z.object({
    code: z.string().optional(),
    state: z.string().optional(),
  }),
  beforeLoad: (options: {
    context: { account: GetAccountResponseDto };
    params: { step: string };
  }) => {
    const { account } = options.context;
    const { step } = options.params;

    if (account.role === 'superadmin') {
      throw redirect({ to: '/superadmin' });
    }

    if (step !== 'complete' && !onboardingSteps.some((candidate) => candidate.key === step)) {
      throw redirect({ to: '/onboarding/$step', params: { step: 'profile' } });
    }

    const selectedOrganizationId = storage.getJSON<Record<string, string>>(
      'selectedOrganizationId',
      {},
    )[account.id] as string | undefined;

    const membership =
      account.memberships?.find(
        (candidate) => candidate.organization.id === selectedOrganizationId,
      ) ?? account.memberships?.[0];

    if (!membership) {
      if (step !== 'profile') {
        throw redirect({ to: '/onboarding/$step', params: { step: 'profile' } });
      }
      return;
    }

    const isPlaceholderName = account.firstName === 'New' && account.lastName === 'User';
    if (isPlaceholderName) {
      if (step !== 'profile') {
        throw redirect({ to: '/onboarding/$step', params: { step: 'profile' } });
      }
      return;
    }

    const { onboarding, isOwner } = membership.organization;
    if (!onboarding || onboarding.completedAt || !isOwner) {
      throw redirect({ to: '/' });
    }
  },
  component: OnboardingStepRoute,
});
